import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import WatchlistDashboardService, { SignalSummary, StrategicOutcomeTab } from './WatchlistDashboardService';
import SignalPoolCard from './components/SignalPoolCard';
import StrategicSection from './components/StrategicSection';
import ActivationSection from './components/ActivationSection';
import ActivityLogsSection from './components/ActivityLogsSection';
import CustomWatchlistSection from './components/CustomWatchlistSection';
import DashboardHeader from './components/DashboardHeader';
import PipelineAnalyticsSection from './components/PipelineAnalyticsSection';

/**
 * Watchlist Dashboard — Internal Admin only.
 * STAGE 1 scope: Layer 1 (Signal Watchlists) — the 6 pool cards. Layers 2/3, custom watchlists,
 * activity logs and the KPI header are added in later stages.
 *
 * institute_id comes from the route param (/support/watchlist-dashboard/:instituteId);
 * an optional ?filter_institute_id= narrows a group to a single campus.
 */
const WatchlistDashboard = () => {
    const { instituteId } = useParams();
    const [searchParams] = useSearchParams();
    // Pre-select the campus from the URL so the filter is never "All campuses" on first load.
    // An explicit ?filter_institute_id= takes precedence; otherwise the route instituteId is used.
    const [filterInstituteId, setFilterInstituteId] = useState<string | null>(
        searchParams.get('filter_institute_id') ?? (instituteId ?? null)
    );

    const [summary, setSummary] = useState<SignalSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [strategicOutcomes, setStrategicOutcomes] = useState<StrategicOutcomeTab[]>([]);

    useEffect(() => {
        if (!instituteId) {
            setError('No institute selected.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        WatchlistDashboardService.getSignalSummary(instituteId, filterInstituteId)
            .then((data) => setSummary(data))
            .catch((err) => setError(err?.message || 'Failed to load watchlist signal pools.'))
            .finally(() => setLoading(false));

        // Layer 2 tabs are driven by the institute's enabled commitment paths.
        WatchlistDashboardService.getStrategicOutcomes(instituteId)
            .then((res) => setStrategicOutcomes(res.outcomes))
            .catch(() => setStrategicOutcomes([]));
    }, [instituteId, filterInstituteId]);

    const poolTotal = summary?.pools.reduce((sum, p) => sum + (p.count || 0), 0) ?? 0;

    return (
        <div className="main p-3 p-md-4">
            <div className="max-1140 mx-auto">
                <div className="row g-4">
                    {/* ── DASHBOARD HEADER: institute identity + campus filter + 6 KPI cards (Stage 6) ── */}
                    {instituteId && (
                        <DashboardHeader
                            instituteId={instituteId}
                            filterInstituteId={filterInstituteId}
                            onFilterChange={setFilterInstituteId}
                        />
                    )}

                    {/* ── LAYER 1 SECTION HEADER (mockup lines 267-272) ── */}
                    <div className="col-12">
                        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 mb-3 px-3 py-3 rounded-3 bg-primary bg-opacity-10 border-4 border-start border-primary">
                            <span className="font-10 font-600 text-uppercase letter-spacing-1 px-2 py-1 rounded-pill bg-primary text-white">
                                Layer 1
                            </span>
                            <span className="font-18 font-700 text-dark-blue">Signal Watchlists</span>
                            <span className="flex-grow-1 font-13 text-secondary-emphasis">
                                Understand who exists in the alumni base — raw visibility, no execution expectation
                            </span>
                            {!loading && !error && (
                                <span className="font-12 font-600 px-2 py-1 rounded-pill bg-white bg-opacity-10 text-primary border border-primary border-opacity-25">
                                    {poolTotal} signals across pools
                                </span>
                            )}
                        </div>

                        {loading && <div className="font-14 text-secondary-emphasis py-4">Loading signal pools…</div>}
                        {error && !loading && (
                            <div className="alert alert-danger font-13" role="alert">
                                {error}
                            </div>
                        )}

                        {!loading && !error && summary && (
                            <div className="row g-3 row-cols-1 row-cols-md-2">
                                {(() => {
                                    const seenIds = new Set<string>();
                                    return summary.pools.map((pool) => {
                                        // From the server's 50 candidates, pick first 5 not yet seen.
                                        const unique5 = pool.top_alumni.filter((a) => !seenIds.has(a.student_id)).slice(0, 5);
                                        // If we couldn't find 5 unique, show the original top-5 (pool is
                                        // genuinely small or heavily overlapping — avoid an empty/short card).
                                        const displayed = unique5.length >= 5 ? unique5 : pool.top_alumni.slice(0, 5);
                                        displayed.forEach((a) => seenIds.add(a.student_id));
                                        const dedupedPool = { ...pool, top_alumni: displayed };
                                        return <SignalPoolCard key={pool.pool} pool={dedupedPool} instituteId={instituteId!} filterInstituteId={filterInstituteId} />;
                                    });
                                })()}
                            </div>
                        )}
                    </div>

                    {/* ── LAYER 2: STRATEGIC — tabs driven by institute.commitment_paths ── */}
                    {instituteId && strategicOutcomes.length > 0 && (
                        <StrategicSection
                            outcomes={strategicOutcomes.map((o) => ({ key: o.key, label: o.label }))}
                            instituteId={instituteId}
                            filterInstituteId={filterInstituteId}
                        />
                    )}

                    {/* ── LAYER 3: ACTIVATION — same enabled outcomes ── */}
                    {instituteId && strategicOutcomes.length > 0 && (
                        <ActivationSection
                            outcomes={strategicOutcomes.map((o) => ({ key: o.key, label: o.label }))}
                            instituteId={instituteId}
                            filterInstituteId={filterInstituteId}
                        />
                    )}

                    {/* ── CUSTOM WATCHLISTS (Stage 5) ── */}
                    {instituteId && <CustomWatchlistSection instituteId={instituteId} filterInstituteId={filterInstituteId} />}

                    {/* ── PIPELINE & GROWTH ANALYTICS (charts) ── */}
                    {instituteId && <PipelineAnalyticsSection instituteId={instituteId} filterInstituteId={filterInstituteId} />}

                    {/* ── ACTIVITY LOGS (Stage 4) ── */}
                    {instituteId && <ActivityLogsSection instituteId={instituteId} filterInstituteId={filterInstituteId} />}
                </div>
            </div>
        </div>
    );
};

export default WatchlistDashboard;
